import { useLazyQuery } from '@apollo/client';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import TextField from '@mui/material/TextField';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import emojiFlags from 'emoji-flags';
import throttle from 'lodash.throttle';
import Router from 'next/router';
import React from 'react';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import { SEARCH } from '../../helpers/graphql/search';
import { getLocationDetails } from '../../helpers/getLocationDetails';
import ProfileAvatar from '../Profile/ProfileAvatar';

// Self-hosted search: destinations come from the backend's in-memory index
// of locations with visible posts (so every suggestion resolves to a
// non-empty destination page), authors from a prefix match on profiles.
// Nothing is fetched until the user starts typing.

// Curated empty-state suggestions. Values must match the location strings
// stored in the database (check the search query before editing) or the
// destination page will 404.
const DEFAULT_SUGGESTIONS = [
  { countryCode: 'id', subdivision: 'Bali', city: null, country: 'Indonesia' },
  { countryCode: 'jp', subdivision: 'Tokyo', city: null, country: 'Japan' },
  {
    countryCode: 'th',
    subdivision: 'Chiang Mai',
    city: null,
    country: 'Thailand',
  },
  {
    countryCode: 'es',
    subdivision: 'Catalonia',
    city: 'Barcelona',
    country: 'Spain',
  },
  {
    countryCode: 'fr',
    subdivision: 'Ile-de-France',
    city: 'Paris',
    country: 'France',
  },
  {
    countryCode: 'us',
    subdivision: 'New York',
    city: 'New York',
    country: 'United States',
  },
].map(destination => ({ ...destination, group: 'Popular destinations' }));

const destinationPrimary = option =>
  option.city || option.subdivision || option.country;

const destinationSecondary = option => {
  if (option.city)
    return [option.subdivision, option.country].filter(Boolean).join(', ');
  if (option.subdivision) return option.country;
  return '';
};

const Highlighted = ({ text, query }) =>
  parse(text, match(text, query)).map((part, index) => (
    <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
      {part.text}
    </span>
  ));

export default function Geocoder(props) {
  const useStyles = makeStyles()(theme => ({
    icon: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(2),
    },
    flag: {
      fontSize: '1.4rem',
      lineHeight: 1,
    },
    optionIcon: {
      minWidth: 40,
    },
    input: {
      borderRadius: theme.shape.borderRadius,
      width: props.width || '90%',
      height: (props.width && '65px') || '100%',
      backgroundColor: alpha(theme.palette.common.white, 0.15),
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
      },
      padding: '5px',
      transition: 'width 0.3s',
    },
    inputField: {
      color: 'white',
    },
    mobileField: {
      padding: '18px',
    },
    inputFocused: {
      width: props.width || '120%',
      backgroundColor: alpha(theme.palette.common.white, 0.25),
      transition: 'width 0.3s',
    },
  }));

  const { classes } = useStyles();
  const [inputValue, setInputValue] = React.useState('');
  const [runSearch, { data, previousData, loading }] = useLazyQuery(SEARCH);

  const throttledSearch = React.useMemo(
    () => throttle(query => runSearch({ variables: { query } }), 200),
    [runSearch],
  );
  React.useEffect(() => () => throttledSearch.cancel(), [throttledSearch]);

  // Keep showing the last results while the next keystroke's query is in
  // flight — Apollo clears `data` on variable change, and an empty options
  // list would make the Autocomplete flash "Loading…" on every letter.
  const serverOptions = React.useMemo(() => {
    const search = data || previousData;
    const result = (search && search.search) || {};
    return [
      ...(result.destinations || []).map(destination => ({
        ...destination,
        group: 'Destinations',
      })),
      ...(result.users || []).map(user => ({ ...user, group: 'Authors' })),
    ];
  }, [data, previousData]);

  // Curated suggestions while the field is empty; live results once typing
  const options = inputValue.trim() ? serverOptions : DEFAULT_SUGGESTIONS;

  const handleSelect = option => {
    if (!option || typeof option === 'string') return;
    if (option.group === 'Authors') {
      Router.push(`/@${option.username}`);
      return;
    }
    Router.push(getLocationDetails(option).locationLink);
  };

  return (
    <Autocomplete
      getOptionLabel={option =>
        typeof option === 'string'
          ? option
          : option.group === 'Authors'
          ? option.displayName || option.username
          : destinationPrimary(option)
      }
      filterOptions={x => x}
      options={options}
      groupBy={option => option.group}
      loading={loading}
      noOptionsText="No results"
      componentsProps={{
        paper: {
          sx: { '& .MuiAutocomplete-listbox': { maxHeight: 'min(560px, 70vh)' } },
        },
        ...(props.isMobile
          ? {}
          : {
              popper: {
                style: { width: 380 },
                placement: 'bottom-start',
              },
            }),
      }}
      autoComplete
      includeInputInList
      value={null}
      isOptionEqualToValue={(option, value) =>
        option.group === value.group &&
        (option.group === 'Authors'
          ? option.username === value.username
          : option.countryCode === value.countryCode &&
            option.subdivision === value.subdivision &&
            option.city === value.city)
      }
      onChange={(event, newValue) => handleSelect(newValue)}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        if (newInputValue.trim()) throttledSearch(newInputValue.trim());
      }}
      renderInput={params => {
        params.InputProps.disableUnderline = true;
        params.InputProps.startAdornment = (
          <InputAdornment position="end">
            <SearchIcon className="text-light ml-1 mr-3" />
          </InputAdornment>
        );
        params.inputProps.className = `${params.inputProps.className} ${classes.inputField}`;
        let textFieldClass = classes.input;
        if (props.isMobile) {
          textFieldClass = classes.mobileField;
          params.InputProps.endAdornment = undefined;
        }
        return (
          <TextField
            variant="standard"
            {...params}
            autoFocus={props.autoFocus}
            placeholder="Search"
            className={textFieldClass}
            classes={{
              focused: classes.inputFocused,
            }}
          />
        );
      }}
      renderOption={(optionProps, option) => {
        if (option.group === 'Authors') {
          return (
            <MenuItem
              className="cpointer"
              {...optionProps}
              key={`author-${option.username}`}
            >
              <ListItemIcon className={classes.optionIcon}>
                <ProfileAvatar author={option.username} size={32} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Highlighted
                    text={option.displayName || option.username}
                    query={inputValue}
                  />
                }
                secondary={`@${option.username}`}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </MenuItem>
          );
        }
        const flagData =
          option.countryCode &&
          emojiFlags.countryCode(option.countryCode.toUpperCase());
        const secondary = destinationSecondary(option);
        return (
          <MenuItem
            className="cpointer"
            {...optionProps}
            key={`destination-${option.countryCode}-${option.subdivision}-${option.city}`}
          >
            <ListItemIcon className={classes.optionIcon}>
              {flagData && flagData.emoji ? (
                <span className={classes.flag}>{flagData.emoji}</span>
              ) : (
                <LocationOnIcon className={classes.icon} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Highlighted
                  text={destinationPrimary(option)}
                  query={inputValue}
                />
              }
              secondary={
                option.postCount != null
                  ? `${secondary ? `${secondary} · ` : ''}${
                      option.postCount
                    } ${option.postCount === 1 ? 'post' : 'posts'}`
                  : secondary
              }
              primaryTypographyProps={{ noWrap: true }}
              secondaryTypographyProps={{ noWrap: true }}
            />
          </MenuItem>
        );
      }}
    />
  );
}
